import React, { useCallback, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import NavBar from "../../components/common/NavBar";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import { alphaFromHex } from "../../util/colors";

const MANAGEMENT_DATA_DASHBOARD = gql`
    query ManagementDataDashboard {
        managementDataDashboard {
            summary {
                totalOrganizations
                activeOrganizations
                clientOrganizations
                activeClientOrganizations
                organizationsWithDivisions
                organizationsWithActiveSeasons
                organizationsWithRegisteredTeams
                organizationsWithRegisteredPlayers
                organizationsWithGames
                organizationsWithCompletedGames
                totalUsers
                activeUsers30Days
                totalGames
                completedGames
            }
            periods {
                key
                label
                days
                organizations
                leagues
                academies
                teams
                divisions
                seasons
                stages
                groups
                teamRegistrations
                playerRegistrations
                users
                activeUsers
                gamesCreated
                completedResults
            }
            organizationTypes {
                typeCode
                typeName
                total
                active
                createdLast30Days
            }
            subscriptionPlans {
                planId
                planName
                application
                organizations
                users
                activeUsers7Days
                activeUsers30Days
                activeUsers90Days
            }
        }
    }
`;

const MANAGEMENT_DATA_ORGANIZATION_HEALTH = gql`
    query ManagementDataOrganizationHealth {
        managementDataOrganizationHealth {
            topOrganizations {
                organizationId
                organizationName
                organizationSlug
                organizationType
                sportName
                city
                state
                country
                divisions
                activeSeasons
                registeredTeams
                registeredPlayers
                games
                completedGames
                adoptionScore
            }
            atRiskOrganizations {
                organizationId
                organizationName
                organizationSlug
                organizationType
                sportName
                city
                state
                country
                divisions
                activeSeasons
                registeredTeams
                registeredPlayers
                games
                completedGames
                adoptionScore
            }
        }
    }
`;

const EMPTY_SUMMARY = {};
const EMPTY_PERIODS = [];
const EMPTY_SUBSCRIPTION_PLANS = [];
const EMPTY_ORGANIZATIONS = [];

function SummaryCard({ icon, label, value, hint, colors }) {
    return (
        <div className="col-12 col-md-6 col-xl-3">
            <div
                style={{
                    borderRadius: 18,
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                    padding: 16,
                    minHeight: 122,
                    display: "flex",
                    gap: 14,
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        width: 46,
                        height: 46,
                        borderRadius: 16,
                        background: colors.primary,
                        color: colors.textOnPrimary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <i className={icon} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            color: "rgba(20,21,26,0.58)",
                        }}
                    >
                        {label}
                    </div>
                    <div
                        style={{
                            marginTop: 5,
                            fontSize: 28,
                            lineHeight: 1,
                            fontWeight: 950,
                            color: "#14151a",
                        }}
                    >
                        {value ?? 0}
                    </div>
                    {hint ? (
                        <div
                            style={{
                                marginTop: 7,
                                fontSize: 12,
                                fontWeight: 800,
                                color: "rgba(20,21,26,0.50)",
                            }}
                        >
                            {hint}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function Panel({ title, subtitle, children, className = "col-12" }) {
    return (
        <div className={className}>
            <div
                style={{
                    borderRadius: 20,
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0 14px 34px rgba(0,0,0,0.09)",
                    padding: 16,
                    minHeight: 340,
                }}
            >
                <div style={{ marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 950, color: "#14151a" }}>
                        {title}
                    </h3>
                    {subtitle ? (
                        <div style={{ marginTop: 4, fontSize: 12.5, fontWeight: 800, color: "rgba(20,21,26,0.55)" }}>
                            {subtitle}
                        </div>
                    ) : null}
                </div>
                {children}
            </div>
        </div>
    );
}

function OrganizationsTable({ title, rows, t }) {
    return (
        <Panel title={title} className="col-12 col-xl-6">
            <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                    <thead>
                    <tr>
                        <th>{t("table.organization")}</th>
                        <th>{t("table.score")}</th>
                        <th>{t("table.divisions")}</th>
                        <th>{t("table.teams")}</th>
                        <th>{t("table.players")}</th>
                        <th>{t("table.completed")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(rows || []).map((row) => (
                        <tr key={row.organizationId}>
                            <td>
                                <div style={{ fontWeight: 900 }}>{row.organizationName}</div>
                                <div style={{ fontSize: 12, color: "rgba(20,21,26,0.52)", fontWeight: 700 }}>
                                    {[row.organizationType, row.sportName, row.city].filter(Boolean).join(" · ")}
                                </div>
                            </td>
                            <td>{row.adoptionScore}%</td>
                            <td>{row.divisions}</td>
                            <td>{row.registeredTeams}</td>
                            <td>{row.registeredPlayers}</td>
                            <td>{row.completedGames}</td>
                        </tr>
                    ))}
                    {!rows?.length ? (
                        <tr>
                            <td colSpan="6" className="text-center text-muted py-4">
                                {t("noData")}
                            </td>
                        </tr>
                    ) : null}
                    </tbody>
                </table>
            </div>
        </Panel>
    );
}

function SubscriptionPlanTable({ title, subtitle, rows, mode, t }) {
    return (
        <Panel title={title} subtitle={subtitle} className="col-12 col-xl-6">
            <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                    <thead>
                    <tr>
                        <th>{t("table.plan")}</th>
                        <th>{mode === "web" ? t("table.organizations") : t("table.users")}</th>
                        {mode === "app" ? (
                            <>
                                <th>{t("table.active7Days")}</th>
                                <th>{t("table.active30Days")}</th>
                                <th>{t("table.active90Days")}</th>
                            </>
                        ) : null}
                    </tr>
                    </thead>
                    <tbody>
                    {(rows || []).map((row) => (
                        <tr key={`${row.application}-${row.planId}-${row.planName}`}>
                            <td style={{ fontWeight: 900 }}>{row.planName}</td>
                            <td>{mode === "web" ? row.organizations : row.users}</td>
                            {mode === "app" ? (
                                <>
                                    <td>{row.activeUsers7Days}</td>
                                    <td>{row.activeUsers30Days}</td>
                                    <td>{row.activeUsers90Days}</td>
                                </>
                            ) : null}
                        </tr>
                    ))}
                    {!rows?.length ? (
                        <tr>
                            <td colSpan={mode === "app" ? 5 : 2} className="text-center text-muted py-4">
                                {t("noData")}
                            </td>
                        </tr>
                    ) : null}
                    </tbody>
                </table>
            </div>
        </Panel>
    );
}

export default function ManagementData() {
    const { t: originalT } = useTranslation();
    const t = useCallback(
        (key, params, fallback) => originalT(`managementData.${key}`, params, fallback),
        [originalT]
    );
    const colors = useThemeColors();
    const { authData } = useActiveProfile();
    const isStaff = Boolean(authData?.user?.isStaff);
    const [activePeriodKey, setActivePeriodKey] = useState("last30Days");

    const { data, loading, error } = useQuery(MANAGEMENT_DATA_DASHBOARD, {
        fetchPolicy: "cache-and-network",
        skip: !isStaff,
    });

    const dashboard = data?.managementDataDashboard;
    const {
        data: healthData,
        loading: healthLoading,
        error: healthError,
    } = useQuery(MANAGEMENT_DATA_ORGANIZATION_HEALTH, {
        fetchPolicy: "cache-and-network",
        skip: !isStaff || !dashboard,
    });
    const organizationHealth = healthData?.managementDataOrganizationHealth;
    const summary = dashboard?.summary || EMPTY_SUMMARY;
    const periods = dashboard?.periods || EMPTY_PERIODS;
    const subscriptionPlans = dashboard?.subscriptionPlans || EMPTY_SUBSCRIPTION_PLANS;
    const activePeriod = periods.find((period) => period.key === activePeriodKey) || periods[1] || periods[0] || {};

    const periodChartData = useMemo(
        () =>
            periods.map((period) => ({
                ...period,
                label: t(`periods.${period.key}`, {}, period.label),
            })),
        [periods, t]
    );

    const activationSteps = useMemo(() => {
        const clients = summary.clientOrganizations || 0;
        return [
            { name: t("funnel.clients"), value: clients },
            { name: t("funnel.divisions"), value: summary.organizationsWithDivisions || 0 },
            { name: t("funnel.seasons"), value: summary.organizationsWithActiveSeasons || 0 },
            { name: t("funnel.teams"), value: summary.organizationsWithRegisteredTeams || 0 },
            { name: t("funnel.players"), value: summary.organizationsWithRegisteredPlayers || 0 },
            { name: t("funnel.results"), value: summary.organizationsWithCompletedGames || 0 },
        ];
    }, [summary, t]);

    const organizationSubscriptionPlans = useMemo(
        () => subscriptionPlans.filter((plan) => plan.application === "web"),
        [subscriptionPlans]
    );

    const userSubscriptionPlans = useMemo(
        () => subscriptionPlans.filter((plan) => plan.application === "app"),
        [subscriptionPlans]
    );

    const organizationSubscriptionChartData = useMemo(
        () =>
            organizationSubscriptionPlans.map((plan) => ({
                name: plan.planName,
                organizations: plan.organizations || 0,
            })),
        [organizationSubscriptionPlans]
    );

    const userSubscriptionChartData = useMemo(
        () =>
            userSubscriptionPlans.map((plan) => ({
                name: plan.planName,
                users: plan.users || 0,
                active30Days: plan.activeUsers30Days || 0,
            })),
        [userSubscriptionPlans]
    );

    const navBarElem = {
        pageTitle: t("title"),
        links: [
            { pageTitle: t("admin"), navLink: null, active: false },
            { pageTitle: t("title"), navLink: "/admin/management-data", active: true },
        ],
    };

    if (!isStaff) {
        return (
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem} />
                <section className="content">
                    <div className="container-fluid">
                        <div className="alert alert-warning">{t("staffOnly")}</div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="content-wrapper" id="kbola-management-data-page">
            <NavBar navBarElem={navBarElem} />
            <section className="content">
                <div className="container-fluid">
                    <div
                        style={{
                            borderRadius: 24,
                            padding: 18,
                            background: `linear-gradient(135deg, ${alphaFromHex(colors.primary, 0.14)}, rgba(255,255,255,1))`,
                            border: "1px solid rgba(0,0,0,0.08)",
                            boxShadow: "0 18px 44px rgba(0,0,0,0.08)",
                            marginBottom: 18,
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 950, color: "#14151a" }}>
                                    {t("title")}
                                </h2>
                                <div style={{ marginTop: 5, fontSize: 13, fontWeight: 800, color: "rgba(20,21,26,0.58)" }}>
                                    {t("subtitle")}
                                </div>
                            </div>
                            <select
                                className="form-control"
                                value={activePeriodKey}
                                onChange={(event) => setActivePeriodKey(event.target.value)}
                                style={{ maxWidth: 220, fontWeight: 800 }}
                                aria-label={t("periodFilter")}
                            >
                                {periods.map((period) => (
                                    <option key={period.key} value={period.key}>
                                        {t(`periods.${period.key}`, {}, period.label)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {error ? (
                        <div className="alert alert-danger">
                            {getLocalizedGraphQLError(error, t, "loadError")}
                        </div>
                    ) : null}

                    {healthError ? (
                        <div className="alert alert-warning">
                            {healthError.message || t("healthLoadError")}
                        </div>
                    ) : null}

                    {loading && !dashboard ? (
                        <div className="text-center text-muted py-5">{t("loading")}</div>
                    ) : (
                        <>
                            <div className="row g-3 mb-3">
                                <SummaryCard
                                    icon="fas fa-building"
                                    label={t("cards.clients")}
                                    value={summary.clientOrganizations}
                                    hint={t("cards.activeClients", { count: summary.activeClientOrganizations || 0 })}
                                    colors={colors}
                                />
                                <SummaryCard
                                    icon="fas fa-user-check"
                                    label={t("cards.activeUsers")}
                                    value={summary.activeUsers30Days}
                                    hint={t("cards.totalUsers", { count: summary.totalUsers || 0 })}
                                    colors={colors}
                                />
                                <SummaryCard
                                    icon="fas fa-users"
                                    label={t("cards.periodRegistrations")}
                                    value={(activePeriod.teamRegistrations || 0) + (activePeriod.playerRegistrations || 0)}
                                    hint={t("cards.selectedPeriod")}
                                    colors={colors}
                                />
                                <SummaryCard
                                    icon="fas fa-clipboard-check"
                                    label={t("cards.completedGames")}
                                    value={summary.completedGames}
                                    hint={t("cards.totalGames", { count: summary.totalGames || 0 })}
                                    colors={colors}
                                />
                            </div>

                            <div className="row g-3 mb-3">
                                <SummaryCard
                                    icon="fas fa-shield-alt"
                                    label={t("periodCards.organizations")}
                                    value={activePeriod.organizations}
                                    hint={t("periodCards.orgBreakdown", {
                                        leagues: activePeriod.leagues || 0,
                                        academies: activePeriod.academies || 0,
                                        teams: activePeriod.teams || 0,
                                    })}
                                    colors={colors}
                                />
                                <SummaryCard
                                    icon="fas fa-layer-group"
                                    label={t("periodCards.structure")}
                                    value={(activePeriod.divisions || 0) + (activePeriod.seasons || 0)}
                                    hint={t("periodCards.structureHint", {
                                        stages: activePeriod.stages || 0,
                                        groups: activePeriod.groups || 0,
                                    })}
                                    colors={colors}
                                />
                                <SummaryCard
                                    icon="fas fa-calendar-plus"
                                    label={t("periodCards.gamesCreated")}
                                    value={activePeriod.gamesCreated}
                                    hint={t("periodCards.results", { count: activePeriod.completedResults || 0 })}
                                    colors={colors}
                                />
                                <SummaryCard
                                    icon="fas fa-user-plus"
                                    label={t("periodCards.newUsers")}
                                    value={activePeriod.users}
                                    hint={t("periodCards.activeUsers", { count: activePeriod.activeUsers || 0 })}
                                    colors={colors}
                                />
                            </div>

                            <div className="row g-3 mb-3">
                                <Panel
                                    title={t("charts.growthTitle")}
                                    subtitle={t("charts.growthSubtitle")}
                                    className="col-12 col-xl-7"
                                >
                                    <div style={{ width: "100%", height: 280 }}>
                                        <ResponsiveContainer>
                                            <LineChart data={periodChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="label" tick={{ fontSize: 12, fontWeight: 700 }} />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Legend />
                                                <Line type="monotone" dataKey="organizations" name={t("chartLabels.organizations")} stroke={colors.primary} strokeWidth={3} />
                                                <Line type="monotone" dataKey="users" name={t("chartLabels.users")} stroke={colors.secondary || "#198754"} strokeWidth={3} />
                                                <Line type="monotone" dataKey="gamesCreated" name={t("chartLabels.games")} stroke={colors.warning || "#ffc107"} strokeWidth={3} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Panel>

                                <Panel
                                    title={t("charts.activationTitle")}
                                    subtitle={t("charts.activationSubtitle")}
                                    className="col-12 col-xl-5"
                                >
                                    <div style={{ width: "100%", height: 280 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={activationSteps} layout="vertical" margin={{ top: 8, right: 20, left: 48, bottom: 8 }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                <XAxis type="number" allowDecimals={false} />
                                                <YAxis type="category" dataKey="name" width={112} tick={{ fontSize: 12, fontWeight: 700 }} />
                                                <Tooltip />
                                                <Bar dataKey="value" name={t("chartLabels.clients")} fill={colors.primary} radius={[0, 10, 10, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Panel>
                            </div>

                            <div className="row g-3 mb-3">
                                <Panel
                                    title={t("charts.organizationSubscriptionsTitle")}
                                    subtitle={t("charts.organizationSubscriptionsSubtitle")}
                                    className="col-12 col-xl-6"
                                >
                                    <div style={{ width: "100%", height: 260 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={organizationSubscriptionChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 700 }} />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Bar dataKey="organizations" name={t("chartLabels.organizations")} fill={colors.primary} radius={[10, 10, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Panel>

                                <Panel
                                    title={t("charts.userSubscriptionsTitle")}
                                    subtitle={t("charts.userSubscriptionsSubtitle")}
                                    className="col-12 col-xl-6"
                                >
                                    <div style={{ width: "100%", height: 260 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={userSubscriptionChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 700 }} />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="users" name={t("chartLabels.users")} fill={colors.primary} radius={[10, 10, 0, 0]} />
                                                <Bar dataKey="active30Days" name={t("chartLabels.active30Days")} fill={colors.secondary || "#198754"} radius={[10, 10, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Panel>
                            </div>

                            <div className="row g-3 mb-3">
                                <SubscriptionPlanTable
                                    title={t("tables.organizationSubscriptions")}
                                    subtitle={t("tables.organizationSubscriptionsSubtitle")}
                                    rows={organizationSubscriptionPlans}
                                    mode="web"
                                    t={t}
                                />
                                <SubscriptionPlanTable
                                    title={t("tables.userSubscriptions")}
                                    subtitle={t("tables.userSubscriptionsSubtitle")}
                                    rows={userSubscriptionPlans}
                                    mode="app"
                                    t={t}
                                />
                            </div>

                            <div className="row g-3 mb-3">
                                <Panel
                                    title={t("charts.organizationTypesTitle")}
                                    subtitle={t("charts.organizationTypesSubtitle")}
                                    className="col-12"
                                >
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover mb-0">
                                            <thead>
                                            <tr>
                                                <th>{t("table.type")}</th>
                                                <th>{t("table.total")}</th>
                                                <th>{t("table.active")}</th>
                                                <th>{t("table.createdLast30Days")}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(dashboard?.organizationTypes || []).map((row) => (
                                                <tr key={row.typeCode || row.typeName}>
                                                    <td>{row.typeName || row.typeCode}</td>
                                                    <td>{row.total}</td>
                                                    <td>{row.active}</td>
                                                    <td>{row.createdLast30Days}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Panel>
                            </div>

                            <div className="row g-3 mb-4">
                                <OrganizationsTable
                                    title={t("tables.topOrganizations")}
                                    rows={organizationHealth?.topOrganizations || EMPTY_ORGANIZATIONS}
                                    t={t}
                                />
                                <OrganizationsTable
                                    title={
                                        healthLoading
                                            ? t("tables.atRiskOrganizationsLoading")
                                            : t("tables.atRiskOrganizations")
                                    }
                                    rows={organizationHealth?.atRiskOrganizations || EMPTY_ORGANIZATIONS}
                                    t={t}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
